import { useSecureGraphqlQuery } from '@psyomics/ui-common';
import {
  type ArticleContent,
  Banner,
  ContentArticle as ContentArticleMolecule,
  GenericError,
  LoadingSpinner,
  toArticleContent,
  useDynamicCrumb,
} from '@psyomics/ui-components';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AnyVariables } from 'urql';

import { useContentArticleBannerContext } from '../../../shared/contexts/content-article-banner-context';
import { ArticleByIdDocument, ArticleByIdQuery } from '../generated/content';

const useArticle = (
  id: string
): { article: ArticleContent | null; fetching: boolean; errorMessage?: 'ContentNotFound' } => {
  const [{ data, fetching, errorMessage }] = useSecureGraphqlQuery<ArticleByIdQuery, AnyVariables, 'ContentNotFound'>(
    {
      pause: !id,
      query: ArticleByIdDocument,
      variables: {
        id,
      },
    },
    ['ContentNotFound']
  );

  const article = toArticleContent(data?.articleById);

  return { article, fetching, errorMessage };
};

type ContentArticleProps = {
  readonly id: string;
};
const ContentArticle = ({ id }: ContentArticleProps) => {
  const { t } = useTranslation();
  const { article, fetching, errorMessage } = useArticle(id);
  useDynamicCrumb(article?.title);
  const { hasDismissedContentArticleBanner, markContentArticleBannerAsDismissed } = useContentArticleBannerContext();
  const showBanner = useRef(!hasDismissedContentArticleBanner).current;

  if (fetching) return <LoadingSpinner />;
  if (errorMessage === 'ContentNotFound') return <GenericError titleKey="page.healthLibrary.errors.notFound" />;
  if (!article) return null;

  return (
    <div>
      {showBanner ? (
        <Banner className="mb-2" onDismiss={markContentArticleBannerAsDismissed}>
          {t('page.healthLibrary.bannerText')}
        </Banner>
      ) : null}
      <ContentArticleMolecule article={article} />
    </div>
  );
};

export { ContentArticle };
